import config from '@/config';
import fetchWrapper from '@/services/fetchWrapper.service';

function buildArtworkAuctionUrl(artworkId, suffix = '') {
  if (!artworkId) {
    throw new Error('Artwork identifier is required.');
  }
  const base = `${config.apis.api.url}/artwork/${artworkId}/auction/bids`;
  if (!suffix) {
    return `${base}/`;
  }
  return `${base}/${suffix.replace(/^[/]+/, '')}`;
}

export async function prepareBidAuthorization({
  artworkId,
  amount,
  currency,
  placedFrom,
  idempotencyKey,
  paymentMethodId,
}) {
  if (!artworkId) {
    throw new Error('Artwork identifier is required to prepare a bid authorization.');
  }
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    throw new Error('A valid bid amount is required to prepare the authorization.');
  }
  if (!paymentMethodId || typeof paymentMethodId !== 'string') {
    throw new Error('Select a payment method before placing your bid.');
  }

  const body = {
    amount: numericAmount,
    payment_method_id: paymentMethodId,
  };

  if (currency) {
    body.currency = currency;
  }
  if (placedFrom) {
    body.placed_from = placedFrom;
  }

  const headers = {};
  if (idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey;
  }

  return fetchWrapper.post(
    buildArtworkAuctionUrl(artworkId, 'payment-intent/'),
    {
      injectToken: true,
      headers,
      body,
    },
  );
}

export async function placeBid({ artworkId, authorizationId, placedFrom, idempotencyKey }) {
  if (!artworkId) {
    throw new Error('Artwork identifier is required to place a bid.');
  }
  if (!authorizationId) {
    throw new Error('Bid authorization identifier is required.');
  }

  const body = {
    authorization_id: authorizationId,
  };

  if (placedFrom) {
    body.placed_from = placedFrom;
  }

  const headers = {};
  if (idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey;
  }

  return fetchWrapper.post(
    buildArtworkAuctionUrl(artworkId),
    {
      injectToken: true,
      headers,
      body,
    },
  );
}

export function submitOrderShippingAddress(orderId, shippingAddress) {
  if (!orderId) {
    throw new Error('Order identifier is required to submit a shipping address.');
  }

  return fetchWrapper.post(
    `${config.apis.api.url}/auction/orders/${orderId}/shipping-address/`,
    {
      injectToken: true,
      body: shippingAddress,
    },
  );
}

export function initiateOrderPaymentIntent(orderId, { paymentMethodId = null, idempotencyKey = null } = {}) {
  if (!orderId) {
    throw new Error('Order identifier is required to initiate the checkout.');
  }

  const payload = {};
  if (paymentMethodId) {
    payload.payment_method_id = paymentMethodId;
  }
  if (idempotencyKey) {
    payload.idempotency_key = idempotencyKey;
  }

  const headers = {};
  if (idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey;
  }

  return fetchWrapper.post(
    `${config.apis.api.url}/auction/orders/${orderId}/payment-intent/`,
    {
      injectToken: true,
      headers: Object.keys(headers).length ? headers : undefined,
      body: payload,
    },
  );
}

const auctionService = {
  prepareBidAuthorization,
  placeBid,
  submitOrderShippingAddress,
  initiateOrderPaymentIntent,
};

export default auctionService;
